// CreateService.tsx
import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@progress/kendo-react-buttons";
import { Typography } from "@progress/kendo-react-common";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import ShadowCard from "../../components/common/ShadowCard";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import FormTextField from "../../components/formFields/FormTextField";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import FormTextArea from "../../components/formFields/FormTextArea";
import FormRichTextField from "../../components/formFields/FormRichTextField";
import FormImageUpload from "../../components/formFields/FormImageUpload";
import { STATUSARRAY } from "../../_contstants/common";
import { getFile, slugify } from "../../_helper/helper";
import {
  createService,
  getServiceByID,
  serviceImageUpload,
  updateService,
} from "./services/service.services";
import { clearServiceDetails } from "./serviceSlice";

const CreateService: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const ServiceID = location.state?.ServiceID;

  const ServiceDetail = useAppSelector((state) => state.service.ServiceDetail);
  const loading = useAppSelector((state) => state.service.formLoading);
  const [formKey, setFormKey] = useState(1);

  useEffect(() => {
    setFormKey((prev) => prev + 1);
  }, [ServiceDetail]);

  useEffect(() => {
    if (ServiceID) {
      dispatch(getServiceByID(ServiceID));
    }
    return () => {
      dispatch(clearServiceDetails());
    };
  }, [ServiceID]);

  const handleSubmit = async (values: any) => {
    // Extract image files before building payload
    const IconImage =
      values?.IconImage && typeof values?.IconImage !== "string"
        ? getFile(values?.IconImage[0] ?? values?.IconImage)
        : "";
    const MainImage =
      values?.MainImage && typeof values?.MainImage !== "string"
        ? getFile(values?.MainImage[0] ?? values?.MainImage)
        : "";
    const BannerImage =
      values?.BannerImage && typeof values?.BannerImage !== "string"
        ? getFile(values?.BannerImage[0] ?? values?.BannerImage)
        : "";

    const hasNewImages = IconImage || MainImage || BannerImage;

    const payload = {
      Title: values.Title ?? "",
      URL: values.URL ?? "",
      Tagline: values.Tagline ?? "",
      ShortDescription: values.ShortDescription ?? "",
      LongDescription: values.LongDescription ?? "",
      KeyFeature: values.KeyFeature ?? "",
      SequenceNo: values.SequenceNo ? Number(values.SequenceNo) : 0,
      MetaTitle: values.MetaTitle ?? "",
      MetaKeywords: values.MetaKeywords ?? "",
      MetaDescription: values.MetaDescription ?? "",
      IsActive: values.IsActive === 1,
    };

    const uploadImages = async (id: number) => {
      if (!hasNewImages) return;
      const imageData = new FormData();
      imageData.append("ID", String(id));
      if (IconImage) imageData.append("IconImage", IconImage);
      if (MainImage) imageData.append("MainImage", MainImage);
      if (BannerImage) imageData.append("BannerImage", BannerImage);
      await dispatch(serviceImageUpload(imageData));
    };

    try {
      if (ServiceID) {
        const response = await dispatch(
          updateService({ ...payload, ID: ServiceID }),
        );
        if (response?.meta?.requestStatus === "fulfilled") {
          await uploadImages(ServiceID);
          dispatch(getServiceByID(ServiceID));
          navigate("/service");
        }
      } else {
        const response = await dispatch(createService(payload));
        const newID = response?.payload?.Data?.ID;
        if (response?.meta?.requestStatus === "fulfilled" && newID) {
          await uploadImages(newID);
          navigate("/service");
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
    }
  };

  const handleCreateSlug = (e: any, formRenderProps: FormRenderProps) => {
    formRenderProps.onChange("URL", { value: slugify(e.value) });
  };

  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      <Form
        onSubmit={handleSubmit}
        key={formKey}
        initialValues={ServiceDetail}
        render={(formRenderProps: FormRenderProps) => (
          <FormElement>
            <ShadowCard style={{ padding: 12 }}>
              <GridLayout
                style={{ marginRight: 30 }}
                gap={{ rows: 5, cols: 10 }}
                cols={[
                  { width: "33.33%" },
                  { width: "33.33%" },
                  { width: "33.33%" },
                ]}
              >
                {/* ── Header ── */}
                <GridLayoutItem colSpan={2}>
                  <Typography.h4 style={{ margin: 0 }}>
                    {ServiceID ? "Update Service" : "Add Service"}
                  </Typography.h4>
                </GridLayoutItem>
                <GridLayoutItem
                  style={{
                    display: "flex",
                    justifyContent: "end",
                    alignItems: "flex-start",
                    gap: 10,
                  }}
                >
                  <ButtonWithLoading
                    label={ServiceID ? "Update" : "Save"}
                    type="submit"
                    disabled={!formRenderProps.allowSubmit || loading}
                    loading={loading}
                  />
                  <Button
                    type="button"
                    fillMode={"outline"}
                    themeColor={"primary"}
                    onClick={() => navigate("/service")}
                  >
                    Cancel
                  </Button>
                </GridLayoutItem>

                {/* ── Basic Info ── */}
                <GridLayoutItem colSpan={3}>
                  <div
                    style={{
                      backgroundColor: "#f8f9fa",
                      padding: "12px 16px",
                      borderRadius: 8,
                      marginTop: 20,
                    }}
                  >
                    <Typography.h5 style={{ margin: 0, color: "#495057" }}>
                      Basic Information
                    </Typography.h5>
                  </div>
                </GridLayoutItem>

                <GridLayoutItem colSpan={2}>
                  <Field
                    name="Title"
                    label="Title"
                    placeholder="Service Title"
                    onChange={(e: any) => handleCreateSlug(e, formRenderProps)}
                    component={FormTextField}
                    validator={requiredValidator}
                    astrike={true}
                  />
                </GridLayoutItem>

                <GridLayoutItem>
                  <Field
                    name="SequenceNo"
                    label="Sequence No"
                    placeholder="e.g., 1"
                    component={FormTextField}
                    type="number"
                  />
                </GridLayoutItem>

                <GridLayoutItem colSpan={2}>
                  <Field
                    name="URL"
                    label="URL / Slug"
                    placeholder="auto-generated-from-title"
                    component={FormTextField}
                    validator={requiredValidator}
                    astrike={true}
                  />
                </GridLayoutItem>

                <GridLayoutItem>
                  <Field
                    name="IsActive"
                    label="Status"
                    placeholder="Select status"
                    component={FormSelectionField}
                    validator={requiredValidator}
                    astrike={true}
                    options={STATUSARRAY?.map((status: any) => ({
                      value: status.value,
                      label: status.label,
                    }))}
                  />
                </GridLayoutItem>
                <GridLayoutItem colSpan={3}>
                  <Field
                    name="Tagline"
                    label="Tagline"
                    placeholder="Short catchy tagline"
                    component={FormTextField}
                  />
                </GridLayoutItem>

                {/* ── Descriptions ── */}
                <GridLayoutItem colSpan={3}>
                  <div
                    style={{
                      backgroundColor: "#f8f9fa",
                      padding: "12px 16px",
                      borderRadius: 8,
                      marginTop: 20,
                    }}
                  >
                    <Typography.h5 style={{ margin: 0, color: "#495057" }}>
                      Content
                    </Typography.h5>
                  </div>
                </GridLayoutItem>

                <GridLayoutItem colSpan={3}>
                  <Field
                    name="ShortDescription"
                    label="Short Description"
                    placeholder="Brief summary of the service"
                    component={FormTextArea}
                    rows={3}
                    validator={requiredValidator}
                    astrike={true}
                  />
                </GridLayoutItem>

                <GridLayoutItem colSpan={3}>
                  <Field
                    name="LongDescription"
                    label="Long Description"
                    component={FormRichTextField}
                  />
                </GridLayoutItem>

                <GridLayoutItem colSpan={3}>
                  <Field
                    name="KeyFeature"
                    label="Key Features"
                    placeholder="e.g., Feature 1, Feature 2, Feature 3"
                    component={FormRichTextField}
                    rows={3}
                  />
                </GridLayoutItem>

                {/* ── Images ── */}
                <GridLayoutItem colSpan={3}>
                  <div
                    style={{
                      backgroundColor: "#f8f9fa",
                      padding: "12px 16px",
                      borderRadius: 8,
                      marginTop: 20,
                    }}
                  >
                    <Typography.h5 style={{ margin: 0, color: "#495057" }}>
                      Images
                    </Typography.h5>
                  </div>
                </GridLayoutItem>

                <GridLayoutItem>
                  <Field
                    justifyContent="start"
                    name="IconImage"
                    label="Icon Image"
                    alternateName="Icon Image"
                    component={FormImageUpload}
                  />
                </GridLayoutItem>

                <GridLayoutItem>
                  <Field
                    justifyContent="start"
                    name="MainImage"
                    label="Main Image"
                    alternateName="Main Image"
                    component={FormImageUpload}
                  />
                </GridLayoutItem>

                <GridLayoutItem>
                  <Field
                    justifyContent="start"
                    name="BannerImage"
                    label="Banner Image"
                    alternateName="Banner Image"
                    component={FormImageUpload}
                  />
                </GridLayoutItem>

                {/* ── SEO ── */}
                <GridLayoutItem colSpan={3}>
                  <hr />
                </GridLayoutItem>

                <GridLayoutItem colSpan={3}>
                  <div
                    style={{
                      backgroundColor: "#f8f9fa",
                      padding: "12px 16px",
                      borderRadius: 8,
                      marginTop: 20,
                    }}
                  >
                    <Typography.h5 style={{ margin: 0, color: "#495057" }}>
                      SEO / Meta
                    </Typography.h5>
                  </div>
                </GridLayoutItem>

                <GridLayoutItem colSpan={3}>
                  <Field
                    name="MetaTitle"
                    label="Meta Title"
                    placeholder="Meta Title"
                    component={FormTextField}
                  />
                </GridLayoutItem>

                <GridLayoutItem colSpan={3}>
                  <Field
                    name="MetaKeywords"
                    label="Meta Keywords"
                    placeholder="keyword1, keyword2, keyword3"
                    component={FormTextField}
                  />
                </GridLayoutItem>

                <GridLayoutItem colSpan={3}>
                  <Field
                    name="MetaDescription"
                    label="Meta Description"
                    placeholder="Meta Description"
                    component={FormTextArea}
                    style={{ height: 100 }}
                  />
                </GridLayoutItem>

                {/* ── Footer Buttons ── */}
                <GridLayoutItem
                  colSpan={3}
                  style={{
                    marginTop: 20,
                    textAlign: "end",
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 10,
                  }}
                >
                  <ButtonWithLoading
                    label={ServiceID ? "Update" : "Save"}
                    type="submit"
                    disabled={!formRenderProps.allowSubmit || loading}
                    loading={loading}
                  />
                  <Button
                    type="button"
                    fillMode={"outline"}
                    themeColor={"primary"}
                    onClick={() => navigate("/service")}
                  >
                    Cancel
                  </Button>
                </GridLayoutItem>
              </GridLayout>
            </ShadowCard>
          </FormElement>
        )}
      />
    </>
  );
};

export default CreateService;
